import { HTMLAttributes, useCallback } from 'react';
import useMediaFile from './useMediaFile';
import { fileConvert, getImageBlob, resourceURLToFile } from 'utils/getImage';
import { isInBrowser } from 'utils/is';

type RequiredAttributes = Required<HTMLAttributes<HTMLElement>>;

const tryWebp2Jpeg = (file: File) => {
  return new Promise<File>((resolve) => {
    if (file.type === 'image/webp') {
      fileConvert(file, 'image/jpeg')
        .then((newFile) => {
          resolve(newFile);
        })
        .catch(() => {
          resolve(file);
        });
      return;
    }
    resolve(file);
  });
};

function useMediaDrop(conversation?: Conversation.Conversation) {
  const onDragEnter = useCallback<RequiredAttributes['onDragEnter']>((e) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const onDragOver = useCallback<RequiredAttributes['onDragOver']>((e) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const onDragEnd = useCallback<RequiredAttributes['onDragEnd']>((e) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const fileManager = useMediaFile(conversation);

  const onDrop = useCallback<RequiredAttributes['onDrop']>(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      const {
        dataTransfer: { files },
      } = e;

      if (files.length === 0) {
        // 尝试获取页面内的资源
        const resourceURL = e.dataTransfer.getData('text/uri-list');
        if (resourceURL) {
          if (resourceURL.endsWith('webp')) {
            // Lazada图片可能是webp格式，因此需要转换格式
            getImageBlob(resourceURL, isInBrowser).then((blob) => {
              const file = new File([blob], `${Date.now}.jpg`, { type: 'image/jpeg' });
              fileManager([file]);
            });
          } else {
            resourceURLToFile(resourceURL).then((file) => {
              fileManager([file]);
            });
          }
        }
        return;
      }

      Promise.all([...files].map(tryWebp2Jpeg)).then((newFiles) => {
        fileManager(newFiles);
      });
    },
    [fileManager],
  );

  return {
    onDragEnter,
    onDragOver,
    onDragEnd,
    onDrop,
  };
}

export default useMediaDrop;
